/**
 * Widget: Team Members Who Explored
 * Shows which team members have explored a specific framework
 */

import React from 'react';
import { Users } from 'lucide-react';
import { mockTeamMembers } from '../../data/mockTeamData';

interface TeamMembersWhoExploredProps {
  frameworkId: string;
  frameworkName?: string;
  maxDisplay?: number;
}

export const TeamMembersWhoExplored: React.FC<TeamMembersWhoExploredProps> = ({
  frameworkId,
  frameworkName,
  maxDisplay = 5,
}) => {
  // Simulate team members who explored this framework
  // In a real app, this would query backend data
  const exploredMembers = mockTeamMembers
    .filter((member) => {
      // Simulate: members with more frameworks explored are more likely
      const hash = frameworkId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const memberHash = member.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const combined = (hash + memberHash) % 100;
      
      // Higher exploration count = higher chance
      if (member.frameworksExplored > 20) return combined < 70;
      if (member.frameworksExplored > 10) return combined < 40;
      return combined < 20;
    })
    .slice(0, maxDisplay);

  if (exploredMembers.length === 0) {
    return null;
  }

  const remainingCount = Math.max(0, mockTeamMembers.length - maxDisplay);

  return (
    <div className="bg-gradient-to-r from-coral-50 to-primary-50 p-4 border border-coral-200">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-coral-600 flex-shrink-0">
          <Users className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-charcoal-900 mb-2">
            Team Members Exploring This{frameworkName ? ` (${frameworkName})` : ''}
          </h3>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex -space-x-2">
              {exploredMembers.map((member) => (
                <div
                  key={member.id}
                  className="relative group"
                  title={`${member.name} - ${member.role}`}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-sage-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                    {member.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()}
                  </div>
                </div>
              ))}
              {remainingCount > 0 && (
                <div className="w-8 h-8 rounded-full bg-charcoal-200 border-2 border-white flex items-center justify-center text-charcoal-600 text-xs font-bold">
                  +{remainingCount}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-1">
            {exploredMembers.slice(0, 3).map((member) => (
              <div
                key={member.id}
                className="text-xs text-charcoal-700 flex items-center gap-2"
              >
                <span className="font-semibold">{member.name}</span>
                <span className="text-charcoal-500">·</span>
                <span className="text-charcoal-600">{member.role}</span>
              </div>
            ))}
            {exploredMembers.length > 3 && (
              <div className="text-xs text-charcoal-500 italic">
                and {exploredMembers.length - 3} more...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

